/**
 * How many short quota windows an account has left before a longer one rolls
 * over.
 *
 * The question an operator actually asks of the rail is not "what percent is
 * used" but "how much more work fits before this stops me". Half of that answer
 * is published — every window carries its utilisation and its next reset — and
 * the other half is arithmetic: a five-hour window that rolls over twenty-nine
 * more times before the weekly cap resets is twenty-nine more fresh allowances.
 *
 * **The length of a window is the one part that may not be known.** Codex
 * publishes it per window (`window_minutes`, carried through as `periodMinutes`);
 * Claude publishes only the next reset. So this module has exactly one assumed
 * figure, `SESSION_PERIOD_MINUTES`, it is used only for the window this console
 * already names by its length, and every result says whether it rested on that
 * assumption — because a projection presented as a measurement is the defect
 * this codebase keeps a separate colour for unmeasured capacity to avoid.
 *
 * Where nothing can be derived the answer is `null`, never zero. Unknown is not
 * zero (CLAUDE.md), and a count of zero would read as "you are out".
 */

import type { UsageWindow } from '@/components/AccountUsage'

/**
 * The length of the short window, for a provider that does not publish one.
 *
 * It is the only invented number in this file, and it is not free-floating: the
 * console has named this window "5 saatlik" / "5-hour" since usage first
 * reached the interface. `quotaWindows.test.ts` fails if the two disagree, so a
 * plan whose session window is not five hours has to be changed in both places
 * or in neither.
 */
export const SESSION_PERIOD_MINUTES = 5 * 60

/** Window kinds that bind the whole subscription rather than one model. A
 *  model-scoped cap is a poor boundary to count towards: it stops that model and
 *  nothing else, so it would answer a narrower question than the one asked. */
const ACCOUNT_WIDE = new Set(['weekly_all', 'monthly_all'])

/** How long a window is, or null when nobody has said. */
export function periodMinutesOf(w: UsageWindow): number | null {
  if (typeof w.periodMinutes === 'number' && w.periodMinutes > 0) return w.periodMinutes
  // Only for the window whose length this console already states in its own
  // label. Every other kind is left unknown rather than guessed at.
  return w.kind === 'session' ? SESSION_PERIOD_MINUTES : null
}

/** True when the length had to be assumed rather than read off the payload. */
export function periodIsAssumed(w: UsageWindow): boolean {
  return !(typeof w.periodMinutes === 'number' && w.periodMinutes > 0)
}

export interface ResetRun {
  /** How many more times `window` rolls over at or before `boundary` does. */
  count: number
  /** The longer window being counted towards. */
  boundary: UsageWindow
  /** Whether the count rested on SESSION_PERIOD_MINUTES rather than on a
   *  published length. The interface says so; it does not quietly round. */
  assumed: boolean
}

function resetMs(w: UsageWindow): number | null {
  if (!w.resetAt) return null
  const ms = Date.parse(w.resetAt)
  return Number.isFinite(ms) ? ms : null
}

/**
 * The longer window this one is counted towards.
 *
 * The *nearest* one that resets after it, not the furthest: with a weekly cap
 * two days out and a monthly one twenty days out, the weekly is what actually
 * stops the operator first, and an answer about the monthly would be true and
 * useless. Account-wide caps are preferred over model-scoped ones for the same
 * reason — see ACCOUNT_WIDE.
 */
function boundaryFor(windows: readonly UsageWindow[], w: UsageWindow): UsageWindow | null {
  const from = resetMs(w)
  if (from == null) return null
  const later = windows.filter((o) => {
    if (o === w) return false
    const at = resetMs(o)
    return at != null && at > from
  })
  if (later.length === 0) return null
  const wide = later.filter((o) => ACCOUNT_WIDE.has(o.kind))
  const pool = wide.length > 0 ? wide : later
  return pool.reduce((best, o) => (resetMs(o)! < resetMs(best)! ? o : best))
}

/**
 * How many more allowances this window gives before the next longer cap resets.
 *
 * Counted from the window's own next reset rather than from now, because that
 * reset is the first of the run: a window resetting in ten minutes with a weekly
 * cap five hours later yields one more allowance, not none.
 *
 * Returns null when anything it rests on is unknown — no length, no reset, no
 * longer window, or a longer window that resets first. Null is "cannot say",
 * which the caller renders as nothing at all.
 */
export function resetsBefore(
  windows: readonly UsageWindow[],
  w: UsageWindow,
): ResetRun | null {
  const period = periodMinutesOf(w)
  if (period == null || period <= 0) return null
  const from = resetMs(w)
  if (from == null) return null

  const boundary = boundaryFor(windows, w)
  if (!boundary) return null
  const until = resetMs(boundary)
  if (until == null) return null

  // The window's own reset counts, so a boundary in the same period still gives
  // one. Anything past that is whole periods.
  const count = Math.floor((until - from) / (period * 60_000)) + 1
  if (count < 1) return null
  return { count, boundary, assumed: periodIsAssumed(w) }
}
